import React from "react";
import {
    isAdminImpersonating,
    sendSessionHeartbeat,
    startViewSession,
    trackEngagementEvent,
    VIEW_TYPES,
} from "../api/engagement";

const HEARTBEAT_MS = 15000;

const newSessionId = () => {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
        return crypto.randomUUID();
    }
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

/**
 * Records one view session per page visit and keeps its duration up to date.
 *
 * Only time with the tab actually visible is counted, so a profile left open in
 * a background tab does not inflate "Average Time Spent". The session id is
 * generated here rather than by the server, so the final flush on unload does
 * not have to wait for a round trip that will not finish.
 */
export const useViewSession = (
    startupId,
    investorId,
    viewType = VIEW_TYPES.STARTUP_PROFILE,
) => {
    React.useEffect(() => {
        if (!startupId || !investorId || isAdminImpersonating()) return;

        const sessionId = newSessionId();
        // Milliseconds of visible time banked so far, plus the moment the
        // current visible stretch began (null while hidden).
        let accumulatedMs = 0;
        let visibleSince =
            typeof document === "undefined" || document.visibilityState === "visible"
                ? Date.now()
                : null;

        const snapshot = () => {
            if (visibleSince !== null) {
                accumulatedMs += Date.now() - visibleSince;
                visibleSince = Date.now();
            }
            return accumulatedMs / 1000;
        };

        const flush = (keepalive) =>
            sendSessionHeartbeat({
                session_id: sessionId,
                investor_id: investorId,
                active_seconds: snapshot(),
                keepalive,
            });

        startViewSession({
            session_id: sessionId,
            startup_id: startupId,
            investor_id: investorId,
            view_type: viewType,
        });

        const interval = setInterval(() => {
            if (document.visibilityState !== "visible") return;
            flush(false);
        }, HEARTBEAT_MS);

        const onVisibilityChange = () => {
            if (document.visibilityState === "hidden") {
                snapshot();
                visibleSince = null;
                flush(true);
            } else {
                visibleSince = Date.now();
            }
        };

        const onPageHide = () => flush(true);

        document.addEventListener("visibilitychange", onVisibilityChange);
        window.addEventListener("pagehide", onPageHide);

        return () => {
            clearInterval(interval);
            document.removeEventListener("visibilitychange", onVisibilityChange);
            window.removeEventListener("pagehide", onPageHide);
            flush(true);
        };
    }, [startupId, investorId, viewType]);
};

/**
 * Returns a stable `track(view_type, target)` for click-level events.
 */
export const useEngagementTracker = (startupId, investorId) => {
    return React.useCallback(
        (viewType, target = "") =>
            trackEngagementEvent({
                startup_id: startupId,
                investor_id: investorId,
                view_type: viewType,
                target,
            }),
        [startupId, investorId],
    );
};
